// src/client/renderer/vfx/env/confetti-pool.ts
//
// Plan 18, Task 5.1 — Confetti rain ceremony pool.
//
// Behaviour contract:
//   - CEREMONY-mode pool. mode: 'ceremony'. Triggered by AwardRevealedEvent.
//   - MULTI-INSTANCE, max-active = 2. Two reveals can chain.
//   - Each emitter is a THREE.Points with ~120 confetti vertices distributed in
//     a ~6-unit horizontal disc above origin (Y=10).
//   - Per-vertex colour cycles deterministically through CONFETTI_COLORS.
//   - Gravity simulation: each particle falls with:
//       initial Y-velocity ≈ 0
//       Y-acceleration ≈ -9.8 units/sec²
//       horizontal drift 0.1–0.4 units/sec (random per particle, deterministic LCG)
//   - update(dt) advances all active emitters' gravity simulation each frame.
//     Position attribute needsUpdate = true after each physics tick.
//   - isFinished(emitter) returns true once elapsed > 4s.
//   - release(emitter) disposes the per-instance geometry + material.
//   - dispose() walks active set + disposes per-instance geometry + material.
//   - No shared geometry (per-vertex velocities are randomized per spawn).
//   - Deterministic RNG via LCG (optional seed injection for tests).

import * as THREE from 'three';
import type {
  EnvCeremonyPool,
  EnvCeremonyPoolDeps,
  EnvCeremonySpawnArgs,
  EnvCeremonyPoolId,
} from './env-ceremony-types';
import type { CeremonyEvent } from './ceremony-events';

// ─────────────────────────────────────────────────────────────────────────────
// Public colour table
// ─────────────────────────────────────────────────────────────────────────────

export const CONFETTI_COLORS = [
  0xff4f4f, // red
  0xffd24a, // gold
  0x6dd56b, // green
  0x6aa9ff, // blue
  0xc063ff, // purple
  0xff7a36, // orange
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Number of confetti particles per emitter. */
const PARTICLE_COUNT = 120;

/** Initial Y position of confetti disc. */
const INITIAL_Y = 10;

/** Horizontal disc radius for initial distribution. */
const DISC_RADIUS = 6;

/** Gravitational acceleration (units/sec²). */
const GRAVITY = -9.8;

/** Emitter lifetime in seconds. */
const LIFETIME = 4.0;

/** Maximum simultaneously active emitters. */
const MAX_ACTIVE = 2;

/** Size of each confetti point (rendered size). */
const POINT_SIZE = 0.3;

// ─────────────────────────────────────────────────────────────────────────────
// LCG RNG (deterministic)
// ─────────────────────────────────────────────────────────────────────────────

function makeLcg(seed: number): () => number {
  let state = seed;
  return function next(): number {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Extended deps — optional seed injection for deterministic tests
// ─────────────────────────────────────────────────────────────────────────────

export interface ConfettiPoolConfig extends EnvCeremonyPoolDeps {
  readonly seed?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface ConfettiEmitterState {
  points: THREE.Points;
  geometry: THREE.BufferGeometry;
  material: THREE.PointsMaterial;
  /** Reference to the BufferAttribute's internal array (shared reference — mutations are visible via getX/getY/getZ). */
  positionAttr: THREE.BufferAttribute;
  /** Per-particle velocity: [vx, vy, vz] interleaved (length = PARTICLE_COUNT * 3). */
  velocityArray: Float32Array;
  elapsed: number;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createConfettiPool({
  scene,
  registry: _registry, // registry not used (no gsap timeline — physics is update-loop driven)
  seed = 8675309,
}: ConfettiPoolConfig): EnvCeremonyPool {
  // The RNG state is shared across all spawns — sequential calls produce
  // different distributions, ensuring two chained reveals look distinct.
  const rng = makeLcg(seed);

  /** Active emitter states. Keyed by THREE.Points instance. */
  const activeEmitters = new Map<THREE.Object3D, ConfettiEmitterState>();

  let disposed = false;

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────

  function buildEmitter(): ConfettiEmitterState {
    const positionArray = new Float32Array(PARTICLE_COUNT * 3);
    const colorArray = new Float32Array(PARTICLE_COUNT * 3);
    const velocityArray = new Float32Array(PARTICLE_COUNT * 3);

    const tempColor = new THREE.Color();

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      // Deterministic horizontal disc distribution via polar coords
      const angle = rng() * Math.PI * 2;
      const radius = rng() * DISC_RADIUS;
      const px = Math.cos(angle) * radius;
      const py = INITIAL_Y;
      const pz = Math.sin(angle) * radius;

      positionArray[i * 3 + 0] = px;
      positionArray[i * 3 + 1] = py;
      positionArray[i * 3 + 2] = pz;

      // Horizontal drift velocities (deterministic per particle)
      // X drift: ±0.4 units/sec range mapped to [-0.4, +0.4]
      const vx = (rng() - 0.5) * 0.8;
      const vz = (rng() - 0.5) * 0.8;
      velocityArray[i * 3 + 0] = vx;
      velocityArray[i * 3 + 1] = 0; // initial Y-velocity = 0
      velocityArray[i * 3 + 2] = vz;

      // Deterministic colour assignment: cycle through CONFETTI_COLORS
      const colorHex = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      tempColor.setHex(colorHex);
      colorArray[i * 3 + 0] = tempColor.r;
      colorArray[i * 3 + 1] = tempColor.g;
      colorArray[i * 3 + 2] = tempColor.b;
    }

    const geometry = new THREE.BufferGeometry();
    const posAttr = new THREE.Float32BufferAttribute(positionArray, 3);
    posAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', posAttr);
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3));

    // After setAttribute, posAttr.array is the internal copy — get reference now.
    // We will mutate posAttr.array directly in update() so getX/getY/getZ are current.
    const posAttrLive = geometry.getAttribute('position') as THREE.BufferAttribute;

    const material = new THREE.PointsMaterial({
      size: POINT_SIZE,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    return {
      points,
      geometry,
      material,
      positionAttr: posAttrLive,
      velocityArray,
      elapsed: 0,
      finished: false,
    };
  }

  function disposeEmitter(state: ConfettiEmitterState): void {
    scene.remove(state.points);
    state.geometry.dispose();
    state.material.dispose();
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Max-active guard: silently drop if at capacity
    if (activeEmitters.size >= MAX_ACTIVE) return null;

    const state = buildEmitter();
    activeEmitters.set(state.points, state);
    return state.points;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed) return new Set();

    const toRemove: THREE.Object3D[] = [];

    for (const [obj, state] of activeEmitters) {
      state.elapsed += deltaSeconds;

      if (state.elapsed >= LIFETIME) {
        state.finished = true;
        toRemove.push(obj);
        continue;
      }

      // Physics: advance each particle position
      const dt = deltaSeconds;
      const posAttr = state.positionAttr;
      const posArr = posAttr.array as Float32Array;
      const vel = state.velocityArray;

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        // Y-velocity increases each tick (gravity)
        vel[i * 3 + 1] += GRAVITY * dt;

        posArr[i * 3 + 0] += vel[i * 3 + 0] * dt;
        posArr[i * 3 + 1] += vel[i * 3 + 1] * dt;
        posArr[i * 3 + 2] += vel[i * 3 + 2] * dt;
      }

      // Mark position attribute as needing GPU upload
      posAttr.needsUpdate = true;
    }

    // Clean up finished emitters
    for (const obj of toRemove) {
      const state = activeEmitters.get(obj);
      if (state) {
        disposeEmitter(state);
        activeEmitters.delete(obj);
      }
    }

    return new Set(activeEmitters.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = activeEmitters.get(emitter);
    if (state) return state.finished;
    // Not in active set → either already disposed or never acquired → finished
    return true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = activeEmitters.get(emitter);
    if (!state) return; // safe no-op for stale/unknown refs

    disposeEmitter(state);
    state.finished = true;
    activeEmitters.delete(emitter);
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose all active emitters (per-instance geometry + material)
    for (const state of activeEmitters.values()) {
      disposeEmitter(state);
    }
    activeEmitters.clear();
  }

  return { mode: 'ceremony', acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a CeremonyEvent to confetti spawn args.
 * Returns null for any event other than award_revealed.
 */
export function resolveConfettiForEvent(
  event: CeremonyEvent,
): { poolId: EnvCeremonyPoolId; args: EnvCeremonySpawnArgs } | null {
  if (event.type !== 'award_revealed') return null;

  return {
    poolId: 'confetti',
    args: {
      category: event.category,
      outcome: event.outcome,
    },
  };
}
